import { onUnmounted, watch } from "vue";
import type { Realtime } from "ably";
import { useStore } from "vuex";
import { MousePositionEventData, CursorWithDrawingData } from "@/models";
import { MODULES, vuexName } from "@/store/utils";

interface UseRealtimeSpaceOptions {
  getRealtime: () => Realtime | undefined;
  resolveChannel: () => string | undefined;
  onCursor?: (payload: MousePositionEventData, senderId?: string) => void;
  onDrawing?: (payload: CursorWithDrawingData, senderId?: string) => void;
  enabled?: () => boolean;
}

export const useRealtimeSpace = (options: UseRealtimeSpaceOptions) => {
  const store = useStore();
  let unregisterHandlers: (() => void) | undefined;
  let isRegistering = false;

  const shouldSendCursor = () => (options.enabled ? options.enabled() : true);

  const ensureHandlers = async () => {
    if (isRegistering) return;

    if (unregisterHandlers) {
      unregisterHandlers();
      unregisterHandlers = undefined;
    }

    if (!options.onCursor && !options.onDrawing) {
      return;
    }

    isRegistering = true;
    try {
      const cleanup = await store.dispatch(vuexName(MODULES.SPACE_PRESENCE.DISPATCHES.REGISTER_HANDLERS), {
        onCursor: options.onCursor,
        onDrawing: options.onDrawing,
      });
      unregisterHandlers = cleanup;
    } finally {
      isRegistering = false;
    }
  };

  watch(
    () => ({
      instance: store.getters[vuexName(MODULES.SPACE_PRESENCE.GETTERS.GET_INSTANCE)],
      spaceChannelName: store.getters[vuexName(MODULES.SPACE_PRESENCE.GETTERS.GET_CHANNEL_NAME)],
    }),
    ({ instance, spaceChannelName }, oldValue) => {
      const spaceReady = instance && spaceChannelName;
      const spaceChanged = spaceChannelName !== oldValue?.spaceChannelName;

      if (spaceReady && (spaceChanged || !oldValue?.instance)) {
        ensureHandlers();
      }
    },
    { immediate: true },
  );

  onUnmounted(() => {
    if (unregisterHandlers) {
      unregisterHandlers();
      unregisterHandlers = undefined;
    }
  });

  const sendCursor = async (payload: MousePositionEventData) => {
    if (!shouldSendCursor()) return;
    const isInitialized = store.getters[vuexName(MODULES.SPACE_PRESENCE.GETTERS.GET_IS_INITIALIZED)];
    if (!isInitialized) return;
    await store.dispatch(vuexName(MODULES.SPACE_PRESENCE.DISPATCHES.SEND_CURSOR), payload);
  };

  const sendLaser = async (payload: CursorWithDrawingData) => {
    if (!shouldSendCursor()) return;
    const isInitialized = store.getters[vuexName(MODULES.SPACE_PRESENCE.GETTERS.GET_IS_INITIALIZED)];
    if (!isInitialized) return;
    await store.dispatch(vuexName(MODULES.SPACE_PRESENCE.DISPATCHES.SEND_LASER), payload);
  };

  const sendPencil = async (payload: CursorWithDrawingData) => {
    if (!shouldSendCursor()) return;
    const isInitialized = store.getters[vuexName(MODULES.SPACE_PRESENCE.GETTERS.GET_IS_INITIALIZED)];
    if (!isInitialized) return;
    await store.dispatch(vuexName(MODULES.SPACE_PRESENCE.DISPATCHES.SEND_PENCIL), payload);
  };

  const disconnect = async () => {
    if (unregisterHandlers) {
      unregisterHandlers();
      unregisterHandlers = undefined;
    }
  };

  return {
    sendCursor,
    sendLaser,
    sendPencil,
    disconnect,
  };
};
